/**
 * RegionalCorp — bucket 6 of 6. Renders the design's four-module Corporate
 * Events layout: Event Calendar · Next 3 Months, Inquiries by Status,
 * Commissions, Upcoming Payouts.
 *
 * The corp-events / inquiry pipeline has no source yet (data model is a
 * separate, not-yet-created Linear project), so the whole surface
 * renders the design layout as an em-dash skeleton behind a single
 * <MockedOverlay>, mirroring RegionalSupplies. It lights up when BON-154
 * lands.
 */
import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, MockedOverlay } from '../primitives';

import { BucketError, BucketLoading } from './BucketStates';
import { CommissionsCard } from './corp/CommissionsCard';
import { EventCalendarCard } from './corp/EventCalendarCard';
import { InquiriesKanbanCard } from './corp/InquiriesKanbanCard';
import { PayoutsCard } from './corp/PayoutsCard';
import type { RegionalBucketProps } from './regional-bucket';
import { useRegionalCorp } from './regionalApi';

export function RegionalCorp({ regionSlug }: RegionalBucketProps) {
  const q = useRegionalCorp(regionSlug);

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <MockedOverlay reason="Corp Events data isn’t wired up yet. Goes live once the corp-events model ships — tracked in BON-154.">
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
          gap: 14,
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <EventCalendarCard />
          <InquiriesKanbanCard />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <CommissionsCard />
          <PayoutsCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
